import { useMemo, useState } from 'react'
import { auroraStore, useAuroraStore } from '../../store/auroraStore'
import {
  activeCycle, focusTargets, levelHistoryBySkill, rubricDomains, stalledSkills,
  type RubricDomain, type RubricSkill,
} from '../../store/selectors'
import { domainColor } from '../../lib/rubric'
import { formatRelative } from '../../lib/dates'
import { LevelBar } from '../rubric/LevelBar'
import { TrendBadge } from '../rubric/TrendBadge'
import { Sparkline } from './Sparkline'

/**
 * The list of what we are tracking.
 *
 * Every axis, grouped by area, with where she is (the level ladder), which
 * way it is moving (a small line over the window), and — for the cycle's
 * focus skills — the target and the date. Unobserved axes are listed, not
 * scored: "not looked at yet" is different from low.
 */

const DAY = 86_400_000

export function TrackList({ window: win = 'cycle' }: { window?: 'cycle' | 'all' }) {
  const domains = useAuroraStore(rubricDomains) as RubricDomain[]
  const history = useAuroraStore(levelHistoryBySkill)
  const targets = useAuroraStore(focusTargets)
  const cycle = useAuroraStore(activeCycle) as any
  const stalled = useAuroraStore(stalledSkills)
  const [openKey, setOpenKey] = useState<string | null>(null)
  const [showAll, setShowAll] = useState<Record<string, boolean>>({})

  const now = Date.now()
  const cycleStart = cycle?.startDate ?? cycle?.start_date
  const cycleEnd = cycle?.endDate ?? cycle?.end_date
  const earliest = useMemo(() => {
    let min = now
    for (const pts of Object.values(history)) if (pts.length && pts[0]!.at < min) min = pts[0]!.at
    return min
  }, [history, now])
  const from = win === 'cycle' && cycleStart ? Date.parse(`${cycleStart}T00:00:00Z`) : earliest - 3 * DAY
  const to = cycleEnd ? Date.parse(`${cycleEnd}T23:59:59Z`) : now + 14 * DAY
  const targetByKey = new Map(targets.map((t) => [t.skillKey, t]))
  const stalledKeys = new Set(stalled.map((x: any) => x.skill?.key))

  const ask = (text: string) => {
    const s = auroraStore.getState() as any
    s.setComposing(text)
    s.setView('thread')
  }

  if (!domains.length) return <p className="muted">No rubric yet.</p>

  // The cycle's focus areas first — they are why this page gets opened —
  // then the rest by how much of them we have actually seen.
  const focusDomains = new Set(targets.map((t) => t.domainKey).filter(Boolean))
  const ordered = [...domains].sort((a, b) => {
    const fa = focusDomains.has(a.key) ? 1 : 0
    const fb = focusDomains.has(b.key) ? 1 : 0
    if (fa !== fb) return fb - fa
    return b.observedCount - a.observedCount
  })

  return (
    <div className="track">
      {ordered.map((d) => {
        const color = domainColor(d.key)
        const observed = d.skills.filter((sk) => typeof sk.level === 'number')
        const unobserved = d.skills.filter((sk) => typeof sk.level !== 'number')
        const focusHere = observed.filter((sk) => targetByKey.has(sk.key)).length
        const isOpen = openKey === d.key || openKey === '*'
        const list = showAll[d.key] ? [...observed, ...unobserved] : observed
        const delta = domainDelta(d, history, from)
        return (
          <section key={d.key} className={`track-domain${isOpen ? ' is-open' : ''}`} style={{ '--domain-color': color } as React.CSSProperties}>
            <button className="track-head" onClick={() => setOpenKey(isOpen ? null : d.key)} aria-expanded={isOpen}>
              <span className="track-swatch" aria-hidden="true" />
              <span className="track-head-text">
                <span className="track-name">{d.name}</span>
                <span className="track-sub">
                  {observed.length ? `${observed.length} of ${d.skills.length} observed` : 'not looked at yet'}
                  {focusHere ? ` · ${focusHere} in focus` : ''}
                  {d.trend === 'stalled' ? ' · something is stuck' : ''}
                </span>
              </span>
              <span className="track-level">
                {observed.length ? (
                  <>
                    <span className="track-level-num tnum">{d.level.toFixed(1)}</span>
                    {delta !== 0 && (
                      <span className={`track-delta tnum ${delta > 0 ? 'is-up' : 'is-down'}`}>
                        {delta > 0 ? '▲' : '▼'} {Math.abs(delta).toFixed(1)}
                      </span>
                    )}
                    <LevelBar level={d.level} domainKey={d.key} size="sm" />
                  </>
                ) : <span className="muted">—</span>}
              </span>
              <span className="track-chevron" aria-hidden="true"><ChevronIcon /></span>
            </button>

            {isOpen && (
              <ul className="track-skills">
                {list.map((sk) => {
                  const t = targetByKey.get(sk.key)
                  const pts = history[sk.key] ?? []
                  const state = skillState(sk.key)
                  return (
                    <li key={sk.key} className={`track-skill${t ? ' is-focus' : ''}${stalledKeys.has(sk.key) ? ' is-stalled' : ''}`}>
                      <button className="track-skill-main" onClick={() => ask(`Tell me about ${sk.name}: where she is, the evidence, and what would move it.`)}>
                        <span className="track-skill-name">
                          {sk.name}
                          {t && <span className="track-target">→ {t.targetLevel ?? '?'} by {shortDate(t.byDate)}</span>}
                        </span>
                        <span className="track-skill-meta">
                          {typeof sk.level === 'number' ? (
                            <>
                              <LevelBar level={sk.level} domainKey={d.key} confidence={sk.confidence} size="sm" />
                              <span className="track-skill-num tnum">{Number.isInteger(sk.level) ? sk.level : sk.level.toFixed(1)}</span>
                              <TrendBadge trend={sk.trend} size="sm" showLabel={false} />
                            </>
                          ) : (
                            <span className="muted">not yet observed</span>
                          )}
                          {state?.lastObservedAt && <span className="track-seen muted">seen {formatRelative(state.lastObservedAt)}</span>}
                          {sk.parentOverride && <span className="track-pin">your call</span>}
                        </span>
                      </button>
                      <span className="track-spark">
                        <Sparkline points={pts} from={from} to={to} color={color} target={t?.targetLevel} />
                      </span>
                    </li>
                  )
                })}
                {unobserved.length > 0 && (
                  <li className="track-more">
                    <button className="btn btn-quiet btn-sm" onClick={() => setShowAll((p) => ({ ...p, [d.key]: !p[d.key] }))}>
                      {showAll[d.key] ? 'Hide' : 'Show'} {unobserved.length} not yet observed
                    </button>
                  </li>
                )}
              </ul>
            )}
          </section>
        )
      })}
    </div>
  )
}

function skillState(key: string): { lastObservedAt?: number } | undefined {
  const s = auroraStore.getState() as any
  const st = Object.values(s.skillStates ?? {}).find((x: any) => (x.skillKey ?? x.skill_key) === key) as any
  if (!st) return undefined
  return { lastObservedAt: st.lastObservedAt ?? st.last_observed_at }
}

/** Domain mean now minus domain mean at the start of the window (observed skills only). */
function domainDelta(d: RubricDomain, history: Record<string, Array<{ at: number; level: number }>>, from: number): number {
  const observed = d.skills.filter((sk) => typeof sk.level === 'number')
  if (!observed.length) return 0
  let sumThen = 0, nThen = 0
  for (const sk of observed) {
    const pts = history[sk.key]
    if (!pts?.length) continue
    let then: number | undefined
    for (const p of pts) { if (p.at <= from) then = p.level; else break }
    if (then === undefined) then = pts[0]!.at <= from ? pts[0]!.level : undefined
    if (then !== undefined) { sumThen += then; nThen++ }
  }
  if (!nThen) return 0
  const now = observed.reduce((a, sk) => a + (sk.level as number), 0) / observed.length
  return Math.round((now - sumThen / nThen) * 10) / 10
}

function shortDate(iso?: string): string {
  if (!iso) return '—'
  return new Date(`${iso}T00:00:00`).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}

const ChevronIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2}
    strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6" /></svg>
)

export type { RubricSkill }
